import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ButtonComponent, colors, FunctionsEnum } from '@watter/shared-components';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CommonTable } from '../../components/common-styles';
import { HeaterLight } from '../../components/icons/heater-light';
import { GET_CURRENT_EARNINGS, GET_CUSTOMER_HEATER_METRICS, GET_HISTORICAL_EARNINGS } from '../../graphql/queries';
import { client } from '../../service/apolloClient';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { formatToUSDate } from '../../utils/helper';
import { MainWrapper } from '../common-styles';
import { AddHeaterWrapper, EditWrapper } from './manage-customer-styles';
import HeaterNamePopup from './update-heater';
import { usePermissions } from '../../custom_hooks/permissions';

const CustomNoRowsOverlay = (): ReactElement => {
    const { t } = useTranslation();
    const activeHTTPRequests = useSelector((state: RootState) => state?.app?.activeHTTPRequests);
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'gray',
            }}
        >
            <Typography>
                {activeHTTPRequests > 0 ? t('data_fetching') : t('earning_utilization_data_unavailable')}
            </Typography>
        </Box>
    );
};
const GpuMetrics = ({ userData, onEditEvent }: any) => {
    const { t } = useTranslation();
    const activeHTTPRequests = useSelector((state: RootState) => state?.app?.activeHTTPRequests);
    const [currentEarnings, setCurrentEarnings] = React.useState<any>();
    const [historicalEarnings, setHistoricalEarnings] = React.useState<any>([]);
    const [showUpdateHeaterModal, setShowUpdateHeaterModal] = useState<boolean>(false);
    const [customerHeaterDetails, setCustomerHeaterDetails] = useState<any>();
    const dispatch = useDispatch();
    const columns: GridColDef[] = [
        { field: 'year', headerName: 'Year', flex: 1 },
        { field: 'month', headerName: 'Month', flex: 1 },
        { field: 'gpu_utilization', headerName: 'GPU Utilisation', flex: 1 },
        // { field: 'earnings', headerName: 'Rent Received', flex: 1 }
    ];

    const getCustomerDetails = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const { data } = await client.query({
                query: GET_CUSTOMER_HEATER_METRICS,
                variables: { userId: userData?.id },
                fetchPolicy: 'network-only',
            });

            if (data?.getCustomerDeviceMetrics) {
                setCustomerHeaterDetails(data?.getCustomerDeviceMetrics);
            }
        } catch (err) {
            console.error('Error fetching customer details:', err);
            dispatch(
                openToast({
                    show: true,
                    message: t('unable_load_gpu_info_msg'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, userData?.id, t]);

    const getCurrentEarnings = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            const { data } = await client.query({
                query: GET_CURRENT_EARNINGS,
                variables: { userId: userData?.id },
                fetchPolicy: 'network-only',
            });

            if (data?.getCustomerCurrentEarnings) {
                const formattedRows = {
                    this_month_earnings: `$${data?.getCustomerCurrentEarnings.this_month_earnings}`,
                    total_earnings: `$${data?.getCustomerCurrentEarnings?.total_earnings}`,
                    vast_start_date: data?.getCustomerCurrentEarnings?.vast_start_date,
                    renting_status: data?.getCustomerCurrentEarnings?.renting_status,
                };
                setCurrentEarnings(formattedRows);
            }
        } catch (err) {
            console.error('Error fetching customer details:', err);
            dispatch(
                openToast({
                    show: true,
                    message: t('unable_load_gpu_info_msg'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, userData?.id, t]);
    const getHistoricalEarnings = React.useCallback(async () => {
        if (!customerHeaterDetails?.device_id) return;
        try {
            dispatch(showLoader());

            const { data } = await client.query({
                query: GET_HISTORICAL_EARNINGS,
                variables: { deviceId: customerHeaterDetails?.device_id },
                fetchPolicy: 'network-only',
            });

            if (data?.getDeviceHistoricalEarnings) {
                const formattedRows = data.getDeviceHistoricalEarnings?.last_6_months_earnings?.map(
                    (
                        row: {
                            year: any;
                            month: any;
                            gpu_utilization: any;
                            earnings: any;
                        },
                        index: any,
                    ) => ({
                        id: index,
                        gpu_utilization: `${row.gpu_utilization}%`,
                        earnings: `$${row?.earnings}`,
                        month: row?.month,
                        year: row?.year,
                    }),
                );
                setHistoricalEarnings(formattedRows);
            }
        } catch (err) {
            console.error('Error fetching customer details:', err);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, customerHeaterDetails?.device_id]);
    useEffect(() => {
        getCurrentEarnings();
        getCustomerDetails();
    }, [userData?.id, getCurrentEarnings, getCustomerDetails]);

    useEffect(() => {
        if (!customerHeaterDetails) return;
        getHistoricalEarnings();
    }, [customerHeaterDetails, getHistoricalEarnings]);

    const handleRefetchData = () => {
        getCurrentEarnings();
        getCustomerDetails();
        onEditEvent();
    };
    const handleCloseVerifyPopUp = () => {
        setShowUpdateHeaterModal(false);
    };
    const handleEditClick = () => {
        setShowUpdateHeaterModal(true);
    };
    const { canCreate } = usePermissions();
    const renderDataView = (): ReactElement => {
        return (
            <>
                <EditWrapper mt={1.5}>
                    <Box className="listBox" mb={2}>
                        <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack>
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Renting_Status')}{' '}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {currentEarnings?.renting_status || '--'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack>
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Started_Date_of_Vast')}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {currentEarnings?.vast_start_date
                                            ? formatToUSDate(currentEarnings?.vast_start_date)
                                            : '--'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack className="cstVerify cstMobileNumber">
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Current_Earnings')}{' '}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {currentEarnings?.this_month_earnings || '--'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack className="cstVerify cstMobileNumber">
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Total_Earnings')}{' '}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {currentEarnings?.total_earnings || '--'}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>
                </EditWrapper>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="h5" color={colors.lightHead} fontWeight={700} letterSpacing={0.5}>
                        {t('GPU_Utilization_Earning_Metrics')}:
                    </Typography>
                    <Typography variant="subtitle1" display="flex" alignItems="center" fontWeight={500}>
                        {t('Last_Six_Months')}
                    </Typography>
                </Stack>
                <CommonTable>
                    <Paper sx={{ width: '100%' }}>
                        <DataGrid
                            rows={historicalEarnings}
                            columns={columns}
                            disableColumnMenu
                            disableColumnSorting
                            hideFooter
                            slots={{
                                noRowsOverlay: CustomNoRowsOverlay,
                            }}
                        />
                    </Paper>
                </CommonTable>
            </>
        );
    };
    const renderHeaterMetricNoData = (): ReactElement => {
        return (
            <AddHeaterWrapper
                mt={1.5}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                }}
            >
                <Stack
                    mb={2}
                    sx={{
                        height: '50vh', // Full viewport height
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 2,
                    }}
                >
                    <HeaterLight />
                    <Typography variant="subtitle1" color="info.100" sx={{ width: 300 }}>
                        {t('add_heater_msg')}
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems="center"
                        justifyContent="space-between"
                        className="actionFooter"
                        spacing={2}
                        pt={2}
                    >
                        <Box className="cstBtnWrapper">
                            {canCreate(FunctionsEnum.MANAGE_CUSTOMERS) && (
                                <ButtonComponent
                                    size="medium"
                                    variant="contained"
                                    fullWidth
                                    buttonName={t('add')}
                                    className="cstBtn"
                                    onClick={handleEditClick}
                                />
                            )}
                        </Box>
                    </Stack>
                </Stack>
            </AddHeaterWrapper>
        );
    };

    const renderMainContainer = (): ReactElement => {
        if (!userData?.serial_id && !currentEarnings) return renderHeaterMetricNoData();
        return renderDataView();
    };
    if (!currentEarnings && activeHTTPRequests > 0)
        return (
            <MainWrapper
                mt={1.5}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                }}
            >
                <Box
                    mb={2}
                    sx={{
                        height: '50vh', // Full viewport height
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 2,
                    }}
                >
                    <Skeleton />
                </Box>
            </MainWrapper>
        );
    return (
        <>
            {renderMainContainer()}
            <HeaterNamePopup
                open={showUpdateHeaterModal}
                closePopup={handleCloseVerifyPopUp}
                heading={t('add_heater_id')}
                userId={userData?.id}
                refetchCustomerDetail={handleRefetchData}
                isEdit={false}
            />
        </>
    );
};
export default GpuMetrics;
